import * as React from "react";
import { api, setBearer, formatApiErrorDetail } from "@/lib/api";

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  React.useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Phase 8 — expose the current user on window so tightly-scoped components
  // (like the optimistic message composer) can render locally-owned rows
  // without threading user through every prop path.
  React.useEffect(() => {
    if (typeof window !== "undefined") window.__fableUser = user || null;
  }, [user]);

  const login = React.useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setBearer(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = React.useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.token) setBearer(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = React.useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* */ }
    setBearer(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const v = React.useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

export { formatApiErrorDetail };
